import { useEffect, useRef } from 'react';
import { useLocation } from '@reach/router';
import {
  START_FETCHING,
  FINISH_FETCHING,
  SET_LEGEND,
  RESET_LEGEND,
  SET_AVAILABLE_YEARS,
  RESET_AVAILABLE_YEARS,
  SET_TIMELINE_FILTER,
  SET_MAP_RENDER_PAYLOAD,
  SET_TABLE_API_DATA,
  RESET_TABLE_API_DATA,
} from './GlobalContextProvider';
import { useGlobalRegion, useGlobalYear, useGlobalDispatch } from './useGlobalState';
import MapApi from './MapApi';
import { getTableParser } from './tableDataParsers';
import mapHelper from './MapHelper';
import getData from './dataService';
import { createPathnameWithoutTrainlingSlash, API_ENDPOINT, CURRENT_YEAR, resolveInitialYear } from './utils';

/**
 * Module-level GeoJSON cache.
 * These assets are large, static for the lifetime of the session, and fetched at most once.
 * Storing them in module scope avoids ref/closure complexity and keeps global state slim.
 */
const geoCache = {
  states: null,
  landkreise: null,
  region2: {}, // keyed by pathname
};

function resolveEffectiveRegion(region, pageData) {
  const regionFilters = pageData?.meta?.regionFilters;
  if (!regionFilters) return null;
  // Carry the current region over when the new view supports it; else use its default.
  if (region !== null && regionFilters.includes(region)) return region;
  return regionFilters[0];
}

function resolvePageYears(pageData, apiTimeline) {
  const configured = pageData?.meta?.years;
  if (Array.isArray(configured) && configured.length > 0) {
    return configured;
  }
  return apiTimeline ?? [];
}

function resolveYearForFetch(isRouteChange, carriedYear, pageYears) {
  if (isRouteChange) {
    // Only trust a carried-over selection here when the page has an explicit
    // year list; otherwise fetch the default and validate the carried year
    // against the backend timeline post-fetch.
    if (carriedYear != null && pageYears.includes(carriedYear)) {
      return carriedYear;
    }
    return resolveInitialYear(pageYears) ?? CURRENT_YEAR;
  }
  if (pageYears.length > 0 && (carriedYear == null || !pageYears.includes(carriedYear))) {
    return resolveInitialYear(pageYears);
  }
  return carriedYear;
}

// Identifies which tooltip content a page renders, without the tooltip renderer having to
// inspect the URL. Falls back to the (always unique) `page` id for pages that don't share
// their `page` id with siblings (e.g. e-mobility); '_total' distinguishes a category's
// "Gesamt" tooltip from its per-item subcategory tooltips when they share a categoryTooltip.
function resolveTooltipKey(pageData) {
  const key = pageData?.meta?.data?.categoryTooltip ?? pageData?.meta?.data?.page ?? null;
  if (!key) return null;
  return pageData.meta.data.isTotal ? `${key}_total` : key;
}

/**
 * Central data orchestrator.
 *
 * Responsible for ALL data fetching in the application. It reads the current
 * navigation state (pathname, region, year) from context + location, fetches
 * both map data and table data in parallel, and distributes the results to
 * global context so that Map and InfoboxTable can be pure consumers.
 *
 * Features:
 * - Single useEffect with AbortController — navigating away cancels in-flight requests
 * - GeoJSON assets are cached at module level and never re-fetched within a session
 * - Parallel fetch of map data and table data
 * - Determines render mode ('full' vs 'ranks-only') so Map knows what to redraw
 */
export default function useDataOrchestrator({ pageData = null }) {
  const location = useLocation();
  const dispatch = useGlobalDispatch();
  const globalRegion = useGlobalRegion();
  const globalYear = useGlobalYear();

  const pathname = createPathnameWithoutTrainlingSlash(location.pathname);
  const categoryTooltip = pageData?.meta?.data?.categoryTooltip ?? null;
  const category = pageData?.meta?.data?.category ?? null;
  const page = pageData?.meta?.data?.page ?? null;
  const markerIcon = pageData?.meta?.data?.markerIcon ?? null;

  const effectiveRegion = resolveEffectiveRegion(globalRegion, pageData);

  // Store values that change together with the tracked deps but shouldn't
  // independently trigger re-runs. Updated synchronously on every render so
  // the effect always reads the latest values via the ref.
  const runParamsRef = useRef(null);
  runParamsRef.current = { location, pageData, page, category, categoryTooltip, markerIcon, dispatch };

  // Track previous values to classify the type of change
  const prevRef = useRef({ pathname: null, region: undefined, year: undefined });
  // When a region/route change is detected, remember that we need a full re-render
  // even if the effect fires a second time due to the cascading SET_TIMELINE_FILTER dispatch.
  const needFullRenderRef = useRef(true);

  useEffect(() => {
    const { location, pageData, page, category, categoryTooltip, markerIcon, dispatch } = runParamsRef.current;

    // Pages without map data (e.g. 404) have no pageData — nothing to fetch.
    if (!pageData?.meta) return;

    const isRouteChange = pathname !== prevRef.current.pathname;
    const isRegionChange = effectiveRegion !== prevRef.current.region;
    const isRegionOrRouteChange = isRouteChange || isRegionChange;

    if (isRegionOrRouteChange) {
      needFullRenderRef.current = true;
    }

    // Nav reset clears the year before the route changes — wait for the new page.
    if (!isRouteChange && globalYear == null) {
      return;
    }

    // Skip the echo run after we sync the Timeline to the year we just resolved.
    if (!isRegionOrRouteChange && globalYear === prevRef.current.year) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const renderMode = needFullRenderRef.current ? 'full' : 'ranks-only';

    // The year active on the previous view, preserved in the ref across navigation
    // (survives the nav reset that clears globalYear) so it can be carried over to
    // the new view when that view's dataset offers it.
    const previousYear = prevRef.current.year;
    const carriedYear = isRouteChange ? previousYear : globalYear;

    const pageYears = resolvePageYears(pageData, []);
    let yearForFetch = resolveYearForFetch(isRouteChange, carriedYear, pageYears);
    prevRef.current = { pathname, region: effectiveRegion, year: yearForFetch };

    const run = async () => {
      dispatch({ type: START_FETCHING });

      try {
        const mapApi = new MapApi(pageData);

        let [mapResult, tableResult] = await Promise.all([
          _fetchMapData(mapApi, effectiveRegion, yearForFetch, pathname, signal),
          _fetchTableData(location, page, yearForFetch, effectiveRegion, category, pageData, signal),
        ]);

        if (signal.aborted) return;

        const timeline = resolvePageYears(pageData, tableResult.data?.metaData?.timeline);

        if (isRouteChange) {
          // Keep the previously selected year when the new view's timeline
          // includes it; otherwise fall back to this view's default.
          const resolvedYear = (previousYear != null && timeline.includes(previousYear))
            ? previousYear
            : resolveInitialYear(timeline);
          if (resolvedYear != null && resolvedYear !== yearForFetch) {
            yearForFetch = resolvedYear;
            [mapResult, tableResult] = await Promise.all([
              _fetchMapData(mapApi, effectiveRegion, yearForFetch, pathname, signal),
              _fetchTableData(location, page, yearForFetch, effectiveRegion, category, pageData, signal),
            ]);
            if (signal.aborted) return;
          }

          prevRef.current = { pathname, region: effectiveRegion, year: yearForFetch };
          dispatch({ type: SET_TIMELINE_FILTER, payload: yearForFetch });
        }

        // --- Distribute table data ---
        if (tableResult.data) {
          dispatch({ type: SET_AVAILABLE_YEARS, payload: timeline });
          dispatch({ type: SET_TABLE_API_DATA, payload: tableResult.parsedData });
        } else {
          dispatch({ type: RESET_AVAILABLE_YEARS });
          dispatch({ type: RESET_TABLE_API_DATA });
        }

        // --- Distribute legend ---
        if (mapResult.ranks?.metaData) {
          dispatch({
            type: SET_LEGEND,
            payload: {
              values2DimArray: mapResult.ranks.metaData.legend,
              unit: mapResult.ranks.metaData.unit,
            },
          });
        } else {
          dispatch({ type: RESET_LEGEND });
        }

        // --- Distribute map render payload ---
        // Map.jsx reacts to this object and drives Leaflet rendering imperatively.
        needFullRenderRef.current = false;
        dispatch({
          type: SET_MAP_RENDER_PAYLOAD,
          payload: {
            renderMode,
            region: effectiveRegion,
            pathname,
            selectedYear: yearForFetch,
            categoryTooltip,
            markerIcon,
            tooltipKey: resolveTooltipKey(pageData),
            statesGeoJson: geoCache.states,
            enrichedLandkreiseGeoJson: mapResult.enrichedLandkreise,
            region2Geos: geoCache.region2[pathname] ?? null,
            ranks: mapResult.ranks,
          },
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        dispatch({ type: RESET_AVAILABLE_YEARS });
        dispatch({ type: RESET_LEGEND });
        dispatch({ type: RESET_TABLE_API_DATA });
      } finally {
        // Always decrement, even on abort — START_FETCHING was already dispatched
        // and pendingRequests must stay balanced.
        dispatch({ type: FINISH_FETCHING });
      }
    };

    run();

    return () => controller.abort();
  }, [pathname, effectiveRegion, globalYear]); // intentional: dispatch and pageData refs are stable
}

async function _fetchMapData(mapApi, region, year, pathname, signal) {
  const result = { ranks: null, enrichedLandkreise: null };

  if (!region || region === 0) {
    if (!geoCache.states) {
      geoCache.states = await mapApi.fetchStates(signal);
    }

    if (year) {
      result.ranks = await mapApi.fetchRanksForStates(year, signal);
    }
  } else if (region === 1) {
    if (!geoCache.landkreise) {
      geoCache.landkreise = await mapApi.fetchLandkreise(signal);
    }

    if (year) {
      result.ranks = await mapApi.fetchRanksForLandkreise(year, signal);
    }

    result.enrichedLandkreise = mapHelper.enrichGeosWithRangs(
      result.ranks?.data ?? null,
      geoCache.landkreise
    );
  } else if (region === 2) {
    if (!geoCache.region2[pathname]) {
      const response = await mapApi.fetchRegion2Geos(signal);
      if (response?.data?.[0]?.features) {
        geoCache.region2[pathname] = response.data[0].features;
      }
    }
  }

  return result;
}

async function _fetchTableData(location, page, year, region, category, pageData, signal) {
  try {
    if (!location || !page) {
      throw new Error('_fetchTableData() is missing data parameters', { location, page });
    }

    const { pathname } = location;
    const regionMap = { 0: 'states', 1: 'districts' };
    const regionPart = `/${regionMap[region] ?? regionMap[0]}`;
    const yearPart = year ? `/${year}` : '';
    const categoryPart = category ? `/${category}` : '';
    const requestURL = `${API_ENDPOINT()}{/page}/top20{/region}{/category}{/year}`
      .replace('{/page}', `/${page}`)
      .replace('{/region}', regionPart)
      .replace('{/category}', categoryPart)
      .replace('{/year}', yearPart);

    const data = await getData(requestURL, { key: [pathname, regionPart, categoryPart, yearPart].filter(Boolean).join(''), signal });
    let parsedData = null;
    try {
      parsedData = getTableParser({ data, pageData, region });
    } catch {
      // Probe years can return a timeline without parsable row data.
    }

    return { data, parsedData };
  } catch {
    return { data: null, parsedData: null };
  }
}
